import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useLocation } from 'react-router-dom';
import ImportedRouteSkeleton, { SKELETON_VARIANTS } from './SkeletonLayouts';

export const RouteSkeleton = ImportedRouteSkeleton;

const RouteTransitionContext = React.createContext({
  isNavigationPending: false,
  isRouteLoading: false,
  registerRouteLoader: () => () => {},
});
const RouteSkeletonVariantContext = React.createContext('content');

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'contents',
  },
  hiddenContent: {
    display: 'none',
  },
}));

// Location state for path changes that stay on the same page (e.g. routed tabs).
export const IN_PAGE_NAVIGATION_STATE = { inPageNavigation: true };

export const RouteTransitionProvider = ({ children, duration = 320 }) => {
  const location = useLocation();
  const routePath = location.pathname;
  const isInPageNavigation = Boolean(location.state && location.state.inPageNavigation);
  const [settledPath, setSettledPath] = React.useState(routePath);
  const [pendingLoaders, setPendingLoaders] = React.useState(0);
  const isNavigationPending = routePath !== settledPath && !isInPageNavigation;
  const isRouteLoading = isNavigationPending || pendingLoaders > 0;

  const registerRouteLoader = React.useCallback(() => {
    let registered = true;
    setPendingLoaders((current) => current + 1);

    return () => {
      if (!registered) {
        return;
      }
      registered = false;
      setPendingLoaders((current) => Math.max(0, current - 1));
    };
  }, []);

  React.useEffect(() => {
    if (routePath === settledPath) {
      return undefined;
    }
    if (isInPageNavigation) {
      setSettledPath(routePath);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSettledPath(routePath);
    }, duration);

    return () => window.clearTimeout(timer);
  }, [duration, isInPageNavigation, routePath, settledPath]);

  const value = React.useMemo(() => ({
    isNavigationPending,
    isRouteLoading,
    registerRouteLoader,
  }), [isNavigationPending, isRouteLoading, registerRouteLoader]);

  return (
    <RouteTransitionContext.Provider value={value}>
      {children}
    </RouteTransitionContext.Provider>
  );
};

RouteTransitionProvider.propTypes = {
  children: PropTypes.node.isRequired,
  duration: PropTypes.number,
};

export const useRouteTransition = () => React.useContext(RouteTransitionContext);

export const RouteSkeletonScope = ({ children, variant = 'content' }) => (
  <RouteSkeletonVariantContext.Provider value={variant}>
    {children}
  </RouteSkeletonVariantContext.Provider>
);

RouteSkeletonScope.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(SKELETON_VARIANTS),
};

export const RouteTransitionContent = ({ children, fullPage = false }) => {
  const classes = useStyles();
  const { isRouteLoading } = useRouteTransition();
  const skeletonVariant = React.useContext(RouteSkeletonVariantContext);

  return (
    <>
      {isRouteLoading ? <RouteSkeleton fullPage={fullPage} variant={skeletonVariant} /> : null}
      <div
        className={isRouteLoading ? classes.hiddenContent : classes.content}
        aria-hidden={isRouteLoading ? 'true' : undefined}
        data-testid="route-content"
      >
        {children}
      </div>
    </>
  );
};

RouteTransitionContent.propTypes = {
  children: PropTypes.node.isRequired,
  fullPage: PropTypes.bool,
};
